
import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card"

import { cn } from "@/lib/utils"
import { useIsMobile } from "@/hooks/use-mobile"

// Set global delay to 0
const TooltipProvider = ({ children, ...props }: TooltipPrimitive.TooltipProviderProps) => (
  <TooltipPrimitive.Provider delayDuration={0} {...props}>
    {children}
  </TooltipPrimitive.Provider>
)

const Tooltip = TooltipPrimitive.Root

const TooltipTrigger = TooltipPrimitive.Trigger

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    sidebarExpanded?: boolean
  }
>(({ className, sideOffset = 4, side = "top", sidebarExpanded, ...props }, ref) => {
  // When sidebar is expanded, force tooltips to the right to avoid overlap
  const dynamicSide = sidebarExpanded ? "right" : side;
  // Increased sideOffset for sidebar expanded scenario
  const dynamicSideOffset = sidebarExpanded ? 124 : sideOffset; // Increased to 124px offset when sidebar is expanded

  return (
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={dynamicSideOffset}
      side={dynamicSide}
      className={cn(
        "z-[200] overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  );
})
TooltipContent.displayName = TooltipPrimitive.Content.displayName

// Create a mobile-friendly tooltip wrapper with no delay
const MobileTooltipWrapper = ({
  children,
  content,
  sidebarExpanded,
  ...rest
}: {
  children: React.ReactNode,
  content: React.ReactNode,
  sidebarExpanded?: boolean,
  [key: string]: any
}) => {
  const isMobile = useIsMobile();
  
  if (isMobile) {
    // Use Popover for mobile - it works with tap/click interactions
    // Force popover to right when sidebar is expanded
    const dynamicSide = sidebarExpanded ? "right" : "top";
    // Increased sideOffset for sidebar expanded scenario
    const dynamicSideOffset = sidebarExpanded ? 124 : 4; // Increased to 124px offset when sidebar is expanded
    
    return (
      <Popover>
        <PopoverTrigger asChild>
          {children}
        </PopoverTrigger>
        <PopoverContent 
          className="w-auto max-w-[280px] p-2 text-sm z-[200]" 
          side={dynamicSide}
          align="start"
          sideOffset={dynamicSideOffset}
          {...rest}
        >
          {content}
        </PopoverContent>
      </Popover>
    );
  }
  
  // Use Tooltip for desktop with no delay
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          {children}
        </TooltipTrigger>
        <TooltipContent sidebarExpanded={sidebarExpanded} {...rest}>
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, MobileTooltipWrapper }
